import React, { useEffect, useState } from 'react';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { StopOutlined } from '@ant-design/icons';
import { spaceId } from 'contentful/contentfulApi';
import { useFrommDataContext } from 'context/frommDataState';
import { r2Url } from 'lib/assetUrl';
import { fabSources } from 'lib/fabMedia';
import { emojis, kaomojis } from 'lib/constants';
import { parseDate } from 'lib/date';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import ImageMedia from 'components/ImageMedia';
import AudioMedia from 'components/AudioMedia';
import VideoMedia from 'components/VideoMedia';
import './ChatBubble.css';

// Convert kaomojis to display properly
const displayKaomojis = (str, index) => {
  for (let i = index; i < kaomojis.length; i++) {
    const kaomoji = kaomojis[i];
    // Check if kaomoji is in str
    if (str.includes(kaomoji)) {
      let textList = str.split(kaomoji);
      // If kaomoji is in str
      if (textList.length > 1) {
        // Change font of the kaomoji
        return (
          <span>
            {displayKaomojis(textList[0], i + 1)}
            <span className="kaomoji-display">{kaomoji}</span>
            {displayKaomojis(textList.slice(1).join(kaomoji), i)}
          </span>
        );
      }
    }
  }
  return str;
};

// Convert emojis to display properly
const displayEmojis = (str, index) => {
  for (let i = index; i < emojis.length; i++) {
    const emoji = emojis[i];
    // Check if emoji is in str
    if (str.includes(emoji)) {
      let textList = str.split(emoji);
      // If emoji is in str
      if (textList.length > 1) {
        // Change font of the emoji
        return (
          <span>
            {displayEmojis(textList[0], i + 1)}
            <span className="emoji-display">{emoji}</span>
            {displayEmojis(textList.slice(1).join(emoji), i)}
          </span>
        );
      }
    }
  }
  return displayKaomojis(str, 0);
};

const mentionedText = (text) => {
  if (!text.includes('@ ')) return errorText(text);
  let textList = text.split('@ ');
  if (textList[0].slice(-1) === '@') {
    return (
      <span>
        {`${textList[0]}@ `}
        {mentionedText(textList[1])}
      </span>
    );
  }
  return (
    <span>
      {textList[0]}
      <span className="mention select-none">크루 </span> {/* 오빛 */}
      {mentionedText(textList[1])}
    </span>
  );
};

const errorText = (text) => {
  if (text.includes('####')) {
    return newLine(text.split('####').join());
  }
  return newLine(text);
};

const newLine = (text) => {
  if (text === '') return <span>{text}</span>;
  return text.split('\n').map((str, index) =>
    index < text.length - 1 ? (
      <span key={`chat-newline-${index}`}>
        {displayEmojis(str, 0)}
        <br />
      </span>
    ) : (
      <span key={`chat-newline-${index}`}>{displayEmojis(str, 0)}</span>
    )
  );
};

/**
 * Chat bubble component.
 * @param {string} dateStr chat date
 * @param {string} text chat text
 * @param {string} type type of the chat
 * @returns Chat bubble component
 */
function ChatBubble({
  service,
  artistNum,
  chatId = null,
  dateStr = null,
  text,
  type,
  emoji = null,
  reply = null,
  mediaurl = null,
  extension = null
}) {
  const { onOpenMedia } = useFrommDataContext();

  const [ImageName, setImageName] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!dateStr) return;
    if (service === 'vlive') {
      setImageName(dateStr.year.slice(2) + dateStr.month + dateStr.date);
    } else {
      const date = parseDate(dateStr);
      setImageName(date.year.slice(2) + date.month + date.date);
    }
  }, [dateStr]);

  // Show loading spinner until media is successfully loaded
  const onMediaLoad = () => {
    setIsLoading(false);
  };

  if (text === 'Deleted') {
    return (
      <div className="bubble deleted">
        <ExclamationCircleOutlined /> 삭제된 메시지입니다.
      </div>
    );
  }

  // Voice
  if (type === 'Voice') {
    let audioSources = [];
    if (service === 'fab') {
      if (text === '' || text === '-') {
        return (
          <div className="bubble voice">
            <StopOutlined />
            <p>음성 댓글</p>
          </div>
        );
      }
      audioSources = fabSources(`${artistNum}/voice/${chatId}_${text}.m4a`);
    } else if (!dateStr || ImageName === '') {
      return <LoadingSpinner />;
    } else if (service === 'vlive') {
      audioSources = [require(`assets/vlive/voice/${ImageName}_${text}.m4a`)];
    } else if (mediaurl && mediaurl !== '' && extension && extension !== '') {
      const base = `${ImageName}_${text}`;
      const file = `${base}.${extension}`;

      // R2 keeps audio under voice
      const exts = [...new Set([extension, extension.toLowerCase()])];
      const r2 = exts.map((ext) =>
        r2Url(`${service}/${artistNum}/voice/${base}.${ext}`)
      );

      // Try R2 -> Contentful
      audioSources = [
        ...r2,
        `https://assets.ctfassets.net/${spaceId}/${mediaurl}/${file}`
      ].filter(Boolean);
    }
    return (
      <div
        className={
          service === 'vlive' ? 'chat-voice-blue' : 'bubble voice audio'
        }
      >
        <AudioMedia sources={audioSources} />
      </div>
    );
  }

  // Image
  if (type === 'Image') {
    if (!dateStr || ImageName === '') {
      return <LoadingSpinner />;
    }

    let image = null;

    if (service === 'vlive') {
      image = require(`assets/vlive/media/${ImageName}_${text}.JPG`);

      return (
        <>
          {isLoading && <LoadingSpinner />}
          <img
            className={`${isLoading && 'hidden'} chat-img-blue`}
            src={image}
            alt=""
            onLoad={onMediaLoad}
            onClick={() => onOpenMedia(true, image, null, null)}
          />
        </>
      );
    }

    let sources = [];

    if (extension && extension !== '') {
      const base = `${ImageName}_${text}`;
      const file = `${base}.${extension}`;

      // R2 is case sensitive so try the extension as is and lowercased
      const exts = [...new Set([extension, extension.toLowerCase()])];
      const r2 = exts.map((ext) =>
        r2Url(`${service}/${artistNum}/media/${base}.${ext}`)
      );

      // Contentful is the last fallback for non migrated images
      const contentful =
        mediaurl && mediaurl !== ''
          ? `https://images.ctfassets.net/${spaceId}/${mediaurl}/${file}`
          : null;

      // Try R2 (both cases), then Contentful
      sources = [...r2, contentful].filter(Boolean);
    }

    return (
      <div className="bubble image">
        {isLoading && <LoadingSpinner />}
        <ImageMedia
          className={`${isLoading && 'hidden'}`}
          sources={sources}
          width={400}
          alt=""
          onLoad={onMediaLoad}
          onError={onMediaLoad}
          onClick={() => onOpenMedia(true, sources, null, null)}
        />
      </div>
    );
  }

  // Video
  if (type === 'Video') {
    if (!dateStr || ImageName === '') return <LoadingSpinner />;

    let sources = [];
    if (extension && extension !== '') {
      const base = `${ImageName}_${text}`;
      const file = `${base}.${extension}`;

      // R2 keeps video under video
      const exts = [...new Set([extension, extension.toLowerCase()])];
      const r2 = exts.map((ext) =>
        r2Url(`${service}/${artistNum}/video/${base}.${ext}`)
      );

      const contentful =
        mediaurl && mediaurl !== ''
          ? `https://videos.ctfassets.net/${spaceId}/${mediaurl}/${file}`
          : null;

      sources = [...r2, contentful].filter(Boolean);
    }

    return <VideoMedia sources={sources} />;
  }

  // Emoji
  if (type === 'Emoji' && emoji) {
    return <p className="bubble-emoji">{displayEmojis(emoji, 0)}</p>;
  }

  // Vlive Text
  if (service === 'vlive') {
    return (
      <div
        className={`${type.includes('Eng') ? 'chat-eng' : ''} chat-bubble-blue`}
      >
        <p className="artist-name">이달의 소녀(LOONA)</p>
        <p>{text}</p>
      </div>
    );
  }

  // Fab Text
  if (service === 'fab') {
    if (reply) {
      return (
        <div className="bubble">
          <div className="bubble-reply">{newLine(reply)}</div>
          {newLine(text)}
        </div>
      );
    }
    return <div className="bubble">{newLine(text)}</div>;
  }

  // Fromm Text
  if (service === 'fromm') {
    return <div className="bubble">{mentionedText(text)}</div>;
  }

  return <div className="bubble"></div>;
}

export default ChatBubble;
